import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertBanner, Button, FooterActionBar } from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import {
  lockReviewAndSubmit,
  lockReviewAndSubmitForConsultee,
  setCertsTabs,
  setReviewAndSubmittingState,
  validateESignature,
} from '../../../redux/certdetails/action';
import { REDIRECT_FLAG } from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import {
  certsFormattedMessage,
  executeBackNavigation,
} from '../../../utils/utils';

import {
  CERTIFICATION_CLOSE_TIMEOUT,
  DEFAULT_LOCK_MESSAGE,
  MINIMUM_LENGTH_OF_COMMENT,
  TAB_STATUS_CONS,
  TAB_STATUS_TODO,
} from './constant';
import { invertedMessages } from './messages';

import './ReviewAndSubmitFooter.css';

function ReviewAndSubmitFooter({
  firstStep,
  isModalOpen,
  setFirstStep,
  setIsAlertActive,
  dataForSubmitAction,
  closeReviewAndSubmitModal,
}) {
  const dispatch = useDispatch();

  const {
    global: { backNavigation, certKey, certificationName },
    reviewAndSubmit,
    tabs: { tabData },
  } = useSelector((state) => state?.certifications?.details);

  const l = useInvertedLocalize(invertedMessages);

  const todoTabCount = tabData?.find(
    (tab) => tab.key === TAB_STATUS_TODO
  )?.count;
  const consultedTabCount = tabData?.find(
    (tab) => tab.key === TAB_STATUS_CONS
  )?.count;
  const hasZeroCountForItems = Boolean(
    todoTabCount === 0 && consultedTabCount === 0
  );

  const shouldDisableNextAndSubmitBtn = Boolean(
    reviewAndSubmit?.isLoadingReviewAndSubmit ||
      reviewAndSubmit?.reviewAndSubmitError ||
      reviewAndSubmit?.isSubmittingReviewAndSubmit
  );

  const [config, setConfig] = useState();
  const [hasSubmittedCertification, setHasSubmittedCertification] =
    useState(false);
  const [alert, setAlert] = useState({
    theme: 'Critical',
    isOpen: false,
    title: '',
    description: '',
    isColonVisible: false,
    autoDismiss: true,
  });

  useEffect(() => {
    if (tabData && reviewAndSubmit) {
      setConfig({
        primaryCertifier: reviewAndSubmit?.primaryCertifier,
        isCommentEnabled: reviewAndSubmit?.isCommentEnabled,
        isComplete: hasZeroCountForItems,
        withESignature: reviewAndSubmit?.withESignature,
      });
    }
  }, [tabData, todoTabCount, reviewAndSubmit]);

  const reviewAndSubmitModalFooter = classnames('ReviewAndSubmitModal-footer');

  const getStatusForFormSubmition = () => {
    if (
      dataForSubmitAction.isPrimaryCertifier &&
      dataForSubmitAction.isESignature
    ) {
      const {
        isCommentEnabled,
        step2SignatureData: {
          userAttributeValue,
          isAgreeChecked,
          commentDescription,
          eSignatureData,
        },
      } = dataForSubmitAction;

      return Boolean(
        userAttributeValue.length > 0 &&
          eSignatureData.length > 0 &&
          isAgreeChecked &&
          ((isCommentEnabled &&
            commentDescription.length <= MINIMUM_LENGTH_OF_COMMENT) ||
            !isCommentEnabled)
      );
    }

    const {
      isCommentEnabled,
      step2CommentData: { commentDescription },
    } = dataForSubmitAction;

    return (
      (isCommentEnabled &&
        commentDescription.length <= MINIMUM_LENGTH_OF_COMMENT) ||
      !isCommentEnabled
    );
  };

  const lockSummary = (comments) => {
    dispatch(lockReviewAndSubmit(certKey, comments))
      .then(() => {
        const callBackNeo = () => {
          const epochTime = Date.now();
          const data = {
            code: epochTime,
            messsage: `Your certification “${certificationName}” has been submitted.`,
          };

          sessionStorage.setItem(REDIRECT_FLAG, JSON.stringify(data));
        };

        const callback = () => {
          setAlert({
            ...alert,
            isOpen: true,
            theme: 'Success',
            title: 'Success',
            description: 'Certification succesfully submitted',
            isColonVisible: true,
            autoDismiss: false,
          });
          setHasSubmittedCertification(true);
        };

        executeBackNavigation(
          backNavigation,
          callBackNeo,
          callback,
          CERTIFICATION_CLOSE_TIMEOUT
        );
      })
      .catch((error) => {
        dispatch(setReviewAndSubmittingState(false));
        setAlert({
          ...alert,
          isOpen: true,
          title: error.message,
        });
      });
  };

  const handleSubmit = () => {
    if (
      dataForSubmitAction.isPrimaryCertifier &&
      dataForSubmitAction.isESignature
    ) {
      const {
        isCommentEnabled,
        step2SignatureData: {
          userAttribute,
          enableloginSignature,
          noOfTries,
          userAttributeValue,
          isAgreeChecked,
          commentDescription,
          eSignatureData,
        },
      } = dataForSubmitAction;
      const shouldEnableAlert = Boolean(
        userAttributeValue.length === 0 ||
          eSignatureData.length === 0 ||
          !isAgreeChecked ||
          (isCommentEnabled &&
            commentDescription.length <= MINIMUM_LENGTH_OF_COMMENT)
      );

      if (shouldEnableAlert) {
        setIsAlertActive(true);

        return;
      }

      const payload = {};

      if (Number(enableloginSignature)) {
        payload[userAttribute] = userAttributeValue;
        payload.userPassword = eSignatureData;
      } else {
        payload.fieldToVerify = userAttribute;
        payload.eSignValueToVerify = userAttributeValue;
        payload.signatureSvg = eSignatureData;
      }

      dispatch(
        validateESignature(certKey, enableloginSignature, noOfTries, payload)
      )
        .then(() => {
          lockSummary(
            isCommentEnabled ? commentDescription : l(DEFAULT_LOCK_MESSAGE)
          );
        })
        .catch((error) => {
          dispatch(setReviewAndSubmittingState(false));
          setAlert({
            ...alert,
            isOpen: true,
            title: error.message,
          });
          setIsAlertActive(true);
        });
    }

    if (!dataForSubmitAction.isESignature) {
      const {
        isCommentEnabled,
        step2CommentData: { commentDescription },
      } = dataForSubmitAction;

      if (
        isCommentEnabled &&
        commentDescription.length <= MINIMUM_LENGTH_OF_COMMENT
      ) {
        setIsAlertActive(true);

        return;
      }

      lockSummary(isCommentEnabled ? commentDescription : DEFAULT_LOCK_MESSAGE);
    }
  };

  const handleNextAndSubmitBtn = () => {
    if ((config?.withESignature || config?.isCommentEnabled) && firstStep) {
      setFirstStep(false);

      return;
    }

    handleSubmit();
  };

  const handleViewListBtn = () => {
    const tabToVisit = todoTabCount === 0 ? TAB_STATUS_CONS : TAB_STATUS_TODO;
    const modifiedTabs = tabData.map((tab) => ({
      ...tab,
      isActive: tab.key === tabToVisit,
      isHighlighted: false,
    }));

    dispatch(setCertsTabs(modifiedTabs));
    closeReviewAndSubmitModal();
  };

  const handleCompleteForConsultee = () => {
    dispatch(lockReviewAndSubmitForConsultee(certKey))
      .then(() => {
        const callBackNeo = () => {
          const epochTime = Date.now();
          const data = {
            code: epochTime,
            messsage: certsFormattedMessage(
              l('Your certification {certificationName} has been submitted.', {
                certificationName,
              }),
              {
                certificationName,
              }
            ),
          };

          sessionStorage.setItem(REDIRECT_FLAG, JSON.stringify(data));
        };

        const callback = () => {
          setAlert({
            ...alert,
            isOpen: true,
            theme: 'Success',
            title: l('Success'),
            description: l('Certification succesfully submitted'),
            isColonVisible: true,
            autoDismiss: false,
          });
          setHasSubmittedCertification(true);
        };

        executeBackNavigation(
          backNavigation,
          callBackNeo,
          callback,
          CERTIFICATION_CLOSE_TIMEOUT
        );
      })
      .catch((error) => {
        dispatch(setReviewAndSubmittingState(false));
        setAlert({
          ...alert,
          isOpen: true,
          title: error.message,
        });
      });
  };

  const handleAlertClose = () => {
    setAlert({
      ...alert,
      isOpen: false,
      title: '',
    });
  };

  useEffect(() => {
    if (!isModalOpen) handleAlertClose();
  }, [isModalOpen]);

  // TODO: @Sagar, Please update this AlertBanner Position.
  return (
    <FooterActionBar className={reviewAndSubmitModalFooter} size='large'>
      <div />
      <div className='ReviewAndSubmitModal-footerButtonWrap'>
        <AlertBanner
          colorTheme={alert.theme}
          title={alert.title}
          description={alert.description}
          isVisible={alert.isOpen}
          isColonVisible={alert.isColonVisible}
          shouldAutoDismiss={alert.autoDismiss}
          onCancel={handleAlertClose}
        />
        <Button
          type='button'
          kind='ghost'
          size='medium'
          onClick={closeReviewAndSubmitModal}
          isDisabled={reviewAndSubmit?.isSubmittingReviewAndSubmit}>
          {l('Cancel')}
        </Button>
        {config?.isComplete && config?.primaryCertifier && (
          <Button
            type='button'
            kind={
              firstStep ||
              shouldDisableNextAndSubmitBtn ||
              getStatusForFormSubmition()
                ? 'filled'
                : 'outlined'
            }
            size='medium'
            isDisabled={shouldDisableNextAndSubmitBtn}
            isLoading={
              reviewAndSubmit?.isSubmittingReviewAndSubmit &&
              !hasSubmittedCertification
            }
            loaderText={firstStep ? l('Next') : l('Submit')}
            className='ReviewAndSubmitModal-footerButtonWrap-primaryAction'
            onClick={handleNextAndSubmitBtn}>
            {(config?.withESignature || config?.isCommentEnabled) && firstStep
              ? l('Next')
              : l('Submit')}
          </Button>
        )}
        {config?.isComplete && !config?.primaryCertifier && (
          <Button
            type='button'
            kind={shouldDisableNextAndSubmitBtn ? 'filled' : 'outlined'}
            size='medium'
            isDisabled={shouldDisableNextAndSubmitBtn}
            isLoading={
              reviewAndSubmit?.isSubmittingReviewAndSubmit &&
              !hasSubmittedCertification
            }
            loaderText='Complete'
            className='ReviewAndSubmitModal-footerButtonWrap-primaryAction'
            onClick={handleCompleteForConsultee}>
            {l('Complete')}
          </Button>
        )}
        {!config?.isComplete && (
          <Button
            type='button'
            kind='filled'
            size='medium'
            className='ReviewAndSubmitModal-footerButtonWrap-primaryAction'
            onClick={handleViewListBtn}>
            {todoTabCount === 0
              ? l('View Consulted List')
              : l('View To-Do List')}
          </Button>
        )}
      </div>
    </FooterActionBar>
  );
}

ReviewAndSubmitFooter.propTypes = {
  isModalOpen: PropTypes.bool.isRequired,
  closeReviewAndSubmitModal: PropTypes.func.isRequired,
  firstStep: PropTypes.bool.isRequired,
  setFirstStep: PropTypes.func.isRequired,
  setIsAlertActive: PropTypes.func,
  dataForSubmitAction: PropTypes.shape({
    isPrimaryCertifier: PropTypes.bool,
    isESignature: PropTypes.bool,
    isCommentEnabled: PropTypes.bool,
    step2CommentData: PropTypes.shape({ commentDescription: PropTypes.string }),
    step2SignatureData: PropTypes.shape({
      userAttribute: PropTypes.string,
      enableloginSignature: PropTypes.string,
      noOfTries: PropTypes.string,
      userAttributeValue: PropTypes.string,
      isAgreeChecked: PropTypes.bool,
      commentDescription: PropTypes.string,
      eSignatureData: PropTypes.string,
    }),
  }),
};

ReviewAndSubmitFooter.defaultProps = {
  setIsAlertActive: () => {},
  dataForSubmitAction: {},
};

export default ReviewAndSubmitFooter;
